import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api, setApiReadOnly } from './api';
import { Company, DEFAULT_COMPANY, companyOf } from '../utils/company';

export interface User {
  id: number;
  email?: string;
  name: string;
  phone?: string;
  role: 'admin' | 'crew' | 'viewer' | 'crew_viewer' | 'sales' | 'manager' | 'bidder' | 'inspector' | 'mechanic';
  roles?: string[];                  // every hat this login may wear (services/roleSwitch)
  choices?: { current: string | null; field: { role: string; label: string; home: string }[]; admin: { role: string; label: string; home: string }[] };
  crewMemberId?: number | null;
  crewId?: number | null;
  /** Ghost crew login only: the crew member whose day it is looking through. */
  shadowing?: string | null;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithToken: (token: string, user: User) => void;
  switchRole: (role: string) => Promise<string>;   // resolves to the home path for that hat
  logout: () => void;
  loading: boolean;
  /** 'production' | 'practice' — from /api/health, known before login. */
  appEnv: string;
  isPractice: boolean;
  /** Business name / phone / email from Settings, via /api/health. */
  company: Company;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [appEnv, setAppEnv] = useState<string>('production');
  const [company, setCompany] = useState<Company>(DEFAULT_COMPANY);

  useEffect(() => {
    // Which server is this? A practice server says so, and every page shows it.
    api.get('/health')
      .then(r => { if (r.data?.app_env) setAppEnv(String(r.data.app_env)); setCompany(companyOf(r.data)); })
      .catch(() => {});
  }, []);

  // Keep the api layer's read-only mirror in step with who is signed in.
  useEffect(() => {
    setApiReadOnly(user?.role === 'viewer' || user?.role === 'crew_viewer');
  }, [user]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    // Only a real "no" from the server signs you out. A call that got no
    // answer — Wi-Fi blip, laptop just woke up, API mid-deploy — used to wipe
    // the token too, so one bad second meant the PIN screen and "Could not
    // connect" (owner, Oct 20 2026). Now it keeps the token and asks again
    // on a backoff; the spinner's banner says what it is waiting on.
    let cancelled = false;
    let attempt = 0;
    const tryMe = () => {
      api.get('/auth/me', { timeout: 15_000 })
        .then(response => { if (!cancelled) { setUser(response.data.user); setLoading(false); } })
        .catch(err => {
          if (cancelled) return;
          if (err?.response) {
            // The server answered: the token is bad or the account is gone.
            localStorage.removeItem('token');
            delete api.defaults.headers.common['Authorization'];
            setLoading(false);
            return;
          }
          attempt += 1;
          setTimeout(tryMe, Math.min(15_000, 3_000 * 2 ** Math.min(attempt - 1, 2)));
        });
    };
    tryMe();
    return () => { cancelled = true; };
  }, []);

  // Email/password login (kept for admin fallback)
  const login = async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    const { token, user: u } = response.data;
    localStorage.setItem('token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(u);
  };

  // Direct token login (used by PIN flow)
  const loginWithToken = (token: string, u: User) => {
    localStorage.setItem('token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(u);
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };
  // One login, several hats (owner, Sep 18 2026): a fresh token in another of this login's roles.
  const switchRole = async (role: string) => {
    const response = await api.post('/auth/switch-role', { role });
    const { token, user: u, home } = response.data;
    localStorage.setItem('token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(u);
    return String(home || '/');
  };

  return (
    <AuthContext.Provider value={{ user, login, loginWithToken, logout, switchRole, loading, appEnv, company, isPractice: appEnv === 'practice' }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
